import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Coins, Plus, Loader2, X } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Floating "+ Credits" FAB — opens a bottom sheet to top up without leaving
 * the current page (e.g. the user's video draft). Appears bottom-right on
 * desktop, bottom-center on mobile.
 */
export default function CreditsFab({ balance = 0, onPurchased }) {
  const [open, setOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [custom, setCustom] = useState('');
  const [loadingId, setLoadingId] = useState(null);
  const [saveCard, setSaveCard] = useState(true);
  const [rate, setRate] = useState(0.65);

  useEffect(() => {
    if (!open) return;
    api.get('/credits/packages').then(r => setPackages(r.data)).catch(() => {});
    api.get('/credits/costs').then(r => setRate(r.data?.rate_usd_per_credit || 0.65)).catch(() => {});
  }, [open]);

  const buy = async (payload, id) => {
    setLoadingId(id);
    try {
      const { data } = await api.post('/credits/checkout', {
        ...payload, origin_url: window.location.origin, save_payment_method: saveCard,
      });
      // Returning user will land back on /payment/success which auto-grants credits
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setLoadingId(null);
    }
  };

  const customCredits = (() => { const v = Number(custom); return !v || v < 5 ? 0 : Math.round(v / rate); })();

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="credits-fab"
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-20 right-4 sm:bottom-6 sm:right-6 inline-flex items-center gap-2 pl-3 pr-4 py-3 rounded-full
                   bg-gradient-to-r from-amber-400 to-orange-500 text-slate-950 font-black shadow-2xl shadow-amber-500/40
                   hover:scale-105 active:scale-95 transition-transform"
        title="Buy credits"
        aria-label="Buy credits"
      >
        <div className="relative">
          <Coins className="w-5 h-5" />
          <Plus className="w-3 h-3 absolute -top-1 -right-1 bg-slate-950 text-amber-300 rounded-full p-[1px]" />
        </div>
        <span className="hidden sm:inline text-sm leading-none">Credits</span>
        <span className="text-xs tabular-nums bg-slate-950/20 px-2 py-0.5 rounded-full">{balance}</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" data-testid="credits-fab-sheet"
          className="rounded-t-3xl bg-slate-950/95 backdrop-blur-xl border-t border-white/10 max-h-[85vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2 font-display text-2xl font-black">
              <Coins className="w-6 h-6 text-amber-400" /> Buy Credits
            </SheetTitle>
            <SheetDescription>
              One-tap top-up — stay on your draft. Your balance is <b className="text-amber-300">{balance} credits</b>.
            </SheetDescription>
          </SheetHeader>

          {/* Package grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
            {packages.map(p => (
              <button
                key={p.id}
                data-testid={`fab-pkg-${p.name.toLowerCase()}`}
                disabled={loadingId === p.id}
                onClick={() => buy({ package_id: p.id }, p.id)}
                className={`relative text-left glass rounded-2xl p-3 border transition-all hover:scale-[1.02] active:scale-[0.98]
                  ${p.featured ? 'border-amber-400/70 ring-2 ring-amber-400/40' : 'border-white/10'}`}
              >
                {p.featured && (
                  <div className="absolute -top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-amber-400 text-slate-950">
                    Best value
                  </div>
                )}
                <div className="text-[11px] uppercase tracking-wider text-slate-400">{p.name}</div>
                <div className="font-display text-2xl font-black">${p.price_usd}</div>
                <div className="text-amber-300 font-bold text-sm flex items-center gap-1 mt-1">
                  <Coins className="w-3.5 h-3.5" /> {p.credits}
                </div>
                {p.est_videos > 0 && (
                  <div className="text-[10px] text-slate-500">~{p.est_videos} videos</div>
                )}
                {loadingId === p.id && (
                  <div className="absolute inset-0 bg-slate-950/60 rounded-2xl grid place-items-center">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-300" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div className="mt-4 glass rounded-2xl p-3 border border-white/10">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-[10px] uppercase tracking-wider">Custom (USD)</Label>
                <Input
                  data-testid="fab-custom-amount"
                  type="number" min="5" step="1" placeholder="5"
                  value={custom} onChange={e => setCustom(e.target.value)}
                />
              </div>
              <div className="pb-1 min-w-[90px] text-amber-300 text-xs font-bold text-right">
                {customCredits ? <>= {customCredits} cr</> : <span className="text-slate-600">— credits</span>}
              </div>
              <Button
                data-testid="fab-custom-buy"
                disabled={loadingId === 'custom' || !customCredits}
                onClick={() => buy({ custom_amount_usd: Number(custom) }, 'custom')}
                className="gradient-brand text-white"
              >
                {loadingId === 'custom' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buy'}
              </Button>
            </div>
            <label className="flex items-center gap-2 mt-2 text-[11px] text-slate-400">
              <input data-testid="fab-save-card" type="checkbox" checked={saveCard} onChange={e => setSaveCard(e.target.checked)} className="accent-violet-500" />
              Save payment method for Auto Top-Up
            </label>
            <div className="text-[10px] text-slate-500 mt-1">Secure checkout.</div>
          </div>

          <button
            onClick={() => setOpen(false)}
            data-testid="fab-close"
            className="mt-3 w-full py-2 text-xs text-slate-500 hover:text-slate-300 inline-flex items-center justify-center gap-1"
          >
            <X className="w-3 h-3" /> Keep editing — close
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
}
